import { useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function ShareModal({ open, onClose, title }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ title, url: shareUrl });
        onClose();
      } catch {
        // 用户取消分享
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-background-950/75" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-lg border border-background-800 bg-background-900 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-base font-bold text-foreground-50">分享影片</h3>
            <p className="mt-1 text-xs text-foreground-400 line-clamp-1">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-700 text-foreground-200 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-700 bg-background-950 px-3 py-2">
          <i className="ri-link text-base text-foreground-400" />
          <input
            readOnly
            value={shareUrl}
            aria-label="分享链接"
            className="flex-1 bg-transparent text-xs text-foreground-300 outline-none"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="rounded bg-primary-500 px-3 py-1 text-xs text-foreground-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            {copied ? "已复制" : "复制"}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-4 gap-3">
          {[
            { icon: "ri-weibo-fill", label: "微博" },
            { icon: "ri-wechat-fill", label: "微信" },
            { icon: "ri-qq-fill", label: "QQ" },
            { icon: "ri-share-forward-fill", label: "更多" },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={item.label === "更多" ? handleNativeShare : handleCopy}
              className="flex flex-col items-center gap-2 rounded-md border border-background-800 py-3 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400 cursor-pointer"
            >
              <i className={`${item.icon} text-xl text-accent-400`} />
              {item.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={handleNativeShare}
          className="mt-5 w-full rounded-md bg-primary-500 py-2.5 text-sm font-medium text-foreground-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          使用系统分享
        </button>
      </div>
    </div>
  );
}